import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const NotificationContext = createContext(null);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        return { notifications: [], unreadCount: 0, markAsRead: () => { }, markAllRead: () => { }, clearNotification: () => { }, clearAll: () => { }, fetchNotifications: () => { } };
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const connectionRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    // Fetch notifications from API
    const fetchNotifications = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const data = await api.getNotifications(1, 50);
            setNotifications(data.notifications || []);
            setUnreadCount(data.unreadCount || 0);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        }
    }, [isAuthenticated]);

    // Mark single notification as read
    const markAsRead = useCallback(async (id) => {
        try {
            await api.markNotificationRead(id);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
        }
    }, []);

    // Mark all as read
    const markAllRead = useCallback(async () => {
        try {
            await api.markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    }, []);

    // Clear single notification (frontend only)
    const clearNotification = useCallback((id) => {
        setNotifications(prev => {
            const removed = prev.find(n => n.id === id);
            if (removed && !removed.isRead) {
                setUnreadCount(c => Math.max(0, c - 1));
            }
            return prev.filter(n => n.id !== id);
        });
    }, []);

    // Clear all notifications (frontend only)
    const clearAll = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
    }, []);

    // Setup SignalR connection
    useEffect(() => {
        if (!isAuthenticated || !user) {
            // Cleanup if logged out
            if (connectionRef.current) {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
            setNotifications([]);
            setUnreadCount(0);
            return;
        }

        const token = localStorage.getItem('alignworks_token');
        if (!token) return;

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5133';

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`${backendUrl}/hubs/notifications`, {
                accessTokenFactory: () => localStorage.getItem('alignworks_token'),
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(signalR.LogLevel.Warning)
            .build();

        // Handle incoming real-time notifications
        connection.on('ReceiveNotification', (notification) => {
            console.log('Real-time notification received:', notification);

            // Add to top of list
            setNotifications(prev => [{
                id: notification.id,
                title: notification.title,
                message: notification.message,
                isRead: false,
                readAt: null,
                createdAt: notification.createdAt,
                eventType: notification.eventType,
                entityType: notification.entityType,
                entityId: notification.entityId,
            }, ...prev]);

            setUnreadCount(prev => prev + 1);

            // Show toast notification
            toast({
                title: notification.title,
                description: notification.message,
            });
        });

        connection.onreconnecting(() => {
            console.log('SignalR reconnecting...');
        });

        connection.onreconnected(() => {
            console.log('SignalR reconnected');
            fetchNotifications(); // Refresh to catch any missed notifications
        });

        connection.onclose(() => {
            console.log('SignalR connection closed');
        });

        // Start connection
        const startConnection = async () => {
            try {
                await connection.start();
                console.log('SignalR connected');
                connectionRef.current = connection;
                // Fetch initial notifications
                fetchNotifications();
            } catch (err) {
                console.error('SignalR connection error:', err);
                // Retry after delay
                reconnectTimeoutRef.current = setTimeout(startConnection, 5000);
            }
        };

        startConnection();

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (connectionRef.current) {
                connectionRef.current.stop();
                connectionRef.current = null;
            }
        };
    }, [isAuthenticated, user, fetchNotifications]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            unreadCount,
            markAsRead,
            markAllRead,
            clearNotification,
            clearAll,
            fetchNotifications,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
